import {
  Chapter,
  DataNode,
  Examination,
  IssueStatus,
  NodeDataType,
  Project,
  Report,
} from "graphql/types"
import { formatMissingValue, formatReportDate } from "utils/reportFormatting"
import { v4 } from "uuid"
import { isDeviceEut } from "./typeGuards"

const createTableNumber = () => {
  return 1
}

const emptyDataNode: DataNode = {
  dataType: NodeDataType.TEXT,
  id: "",
  text: "",
  editable: false,
  deletable: false,
  image: [],
  imageCaption: [],
  imageNumber: [],
  tableBody: [],
  tableCaption: "",
  tableHeader: [],
  tableNumber: 0,
}

// Create Cover Page
export const getCoverPage = (report: Report, project: Project): DataNode[] => {
  const issueStatus = report.issueStatus || IssueStatus.DRAFT

  const classificationText = "Not classified"

  // Classification box at top
  const classificationBox: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: false,
    deletable: false,
    tableCaption: "",
    tableHeader: [],
    tableBody: [[{ data: `**${classificationText.toUpperCase()}**`, colSpan: 1 }]],
  }

  // Main title block - centered and formal
  const titleBlock: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `\n\n\n# PRÜFBERICHT\n# LABORATORY TEST REPORT\n\n---\n\n`,
    editable: false,
    deletable: false,
  }

  // Document information table
  const documentInfoTable: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: false,
    deletable: false,
    tableCaption: "",
    tableHeader: [],
    tableBody: [
      [
        { data: "**Report No.:**", colSpan: 1 },
        { data: formatMissingValue(report.documentNo), colSpan: 1 },
      ],
      [
        { data: "**Issue Status:**", colSpan: 1 },
        { data: issueStatus, colSpan: 1 },
      ],
      [
        { data: "**Assignment No.:**", colSpan: 1 },
        { data: formatMissingValue(report.assignmentNo), colSpan: 1 },
      ],
      [
        { data: "**Release Date:**", colSpan: 1 },
        { data: formatReportDate(report.releaseDate), colSpan: 1 },
      ],
    ],
  }

  // Equipment under test section
  const equipmentSection: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `\n## Equipment Under Test\n\n**${
      report.examinations[0]?.deviceSetup
        ?.filter((ds) => isDeviceEut(ds.device))
        ?.map((ds) => ds.device.name)
        .join(", ") || "./."
    }**`,
    editable: false,
    deletable: false,
  }

  // Client information block - more structured
  const customer: any = (report as any)?.customer
  const clientName =
    customer?.customerName ||
    customer?.legalName ||
    (project as any)?.lead?.businessAccount ||
    project.name

  const addressLines: string[] = []
  if (customer?.addressLine1) addressLines.push(customer.addressLine1)
  const cityLine = [customer?.postalCode, customer?.city].filter(Boolean).join(" ")
  if (cityLine) addressLines.push(cityLine)
  if (customer?.country) addressLines.push(customer.country)
  const clientAddress = addressLines.join("\n")

  const clientBlock: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: "Client Information",
    tableHeader: [],
    tableBody: [
      [
        { data: "**Company:**", colSpan: 1 },
        { data: formatMissingValue(clientName), colSpan: 2 },
      ],
      [
        { data: "**Address:**", colSpan: 1 },
        { data: formatMissingValue(clientAddress), colSpan: 2 },
      ],
      [
        { data: "**Contact Person:**", colSpan: 1 },
        { data: formatMissingValue(project.lead?.ownerEmployeeName), colSpan: 2 },
      ],
    ],
  }

  // Test facility information - restructured
  const testFacilityBlock: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: false,
    deletable: false,
    tableCaption: "Test Facility",
    tableHeader: [],
    tableBody: [
      [
        { data: "**Location:**", colSpan: 1 },
        { data: report.testFacility || "TREO-Neumünster", colSpan: 2 },
      ],
      [
        { data: "**Anschrift / Address:**", colSpan: 1 },
        { data: "Donaubogen 5\nD-24539 Neumünster", colSpan: 2 },
      ],
      [
        { data: "**Kontakt / Contact:**", colSpan: 1 },
        { data: "Tel: +49 (0)4321 965 32 - 35\nFax: +49 (0)40 709 73 76 - 25", colSpan: 2 },
      ],
    ],
  }

  // Approval signatures section - formal layout
  const approvalSection: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: "Freigabe / Approval",
    tableHeader: [],
    tableBody: [
      [
        { data: "**Geprüft von / Tested by:**", colSpan: 1 },
        { data: report.createdBy?.displayName || "", colSpan: 1 },
        { data: "Datum / Date: _____________", colSpan: 1 },
      ],
      [
        { data: "", colSpan: 1 },
        { data: "Signature: _____________", colSpan: 2 },
      ],
      [
        { data: "**Freigegeben von / Released by:**", colSpan: 1 },
        {
          data: report.reviewedBy
            ? `${report.reviewedBy.firstName} ${report.reviewedBy.lastName}`
            : "",
          colSpan: 1,
        },
        { data: "Datum / Date: _____________", colSpan: 1 },
      ],
      [
        { data: "", colSpan: 1 },
        { data: `${report.reviewerTitle || "Laborleiter / Laboratory Manager"}`, colSpan: 1 },
        { data: "", colSpan: 1 },
      ],
      [
        { data: "", colSpan: 1 },
        { data: "Signature: _____________", colSpan: 2 },
      ],
    ],
  }

  // Accreditation notice
  const accreditationNotice: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `\n---\n\n**DAkkS-Akkreditierung / DAkkS Accreditation**\n\nTreo ist ein von der Deutschen Akkreditierungsstelle GmbH (DAkkS) nach DIN EN ISO/IEC 17025 akkreditiertes Prüflaboratorium. Die Akkreditierung gilt nur für die in der Urkundenanlage aufgeführten Prüfgebiete.\n\n*Treo is an accredited testing laboratory by the Deutsche Akkreditierungsstelle GmbH (DAkkS) according to DIN EN ISO/IEC 17025. The accreditation is valid only for the test areas listed in the accreditation certificate.*`,
    editable: false,
    deletable: false,
  }

  // Form footer with document control
  const formFooter: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `\n\n---\n\n${formatMissingValue(report.formNumber)} Rev ${formatMissingValue(
      report.revisions && report.revisions.length > 0
        ? report.revisions[report.revisions.length - 1].revisionNumber
        : null
    )} | Seite / Page 1`,
    editable: false,
    deletable: false,
  }

  return [
    classificationBox,
    titleBlock,
    documentInfoTable,
    equipmentSection,
    clientBlock,
    testFacilityBlock,
    approvalSection,
    accreditationNotice,
    formFooter,
  ]
}

// Create Revision History Table
export const getRevisionHistory = (report: Report): DataNode[] => {
  const revisions = report.revisions || []

  const tableBody =
    revisions.length > 0
      ? revisions.map((rev) => [
          { data: rev.revisionNumber, colSpan: 1 },
          { data: rev.date, colSpan: 1 },
          { data: rev.affectedSections, colSpan: 1 },
          { data: `${rev.issuedBy?.firstName || ""} ${rev.issuedBy?.lastName || ""}`, colSpan: 1 },
          { data: rev.reasonsForRevision, colSpan: 1 },
        ])
      : [
          [
            { data: report.issueStatus === IssueStatus.DRAFT ? "DRAFT" : "1.0", colSpan: 1 },
            { data: report.releaseDate || new Date().toISOString().split("T")[0], colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: report.createdBy?.displayName || report.createdBy?.email || "", colSpan: 1 },
            { data: "Initial Release", colSpan: 1 },
          ],
        ]

  const revisionTable: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableNumber: createTableNumber(),
    tableCaption: "List of Revisions",
    tableHeader: [
      { data: "Issue", colSpan: 1 },
      { data: "Date", colSpan: 1 },
      { data: "Affected Section", colSpan: 1 },
      { data: "Issued by", colSpan: 1 },
      { data: "Reasons for Revision", colSpan: 1 },
    ],
    tableBody: tableBody,
  }

  return [revisionTable]
}

// Create Table of Contents with dynamic chapter structure
export const getTableOfContents = (report: Report, chapters: Chapter[] = []): DataNode[] => {
  // Build table of contents dynamically based on actual chapters
  let tocContent = "# Index\n\n"
  const pageNum = 1

  chapters.forEach((chapter, idx) => {
    if (chapter.name === "Cover Page" || chapter.name === "List of Revisions") {
      // Skip these in TOC
      return
    }

    if (chapter.name === "Table of Contents") {
      tocContent += `${idx}. Index ... Page ${pageNum}\n`
    } else {
      tocContent += `${chapter.chapterNumber || idx}. ${chapter.name} ... Page ${pageNum + idx * 3}\n`

      // Add sub-chapters if they exist
      chapter.subChapter?.forEach((subChap, subIdx) => {
        tocContent += `  ${chapter.chapterNumber || idx}.${subIdx + 1} ${subChap.name} ... Page ${pageNum + idx * 3 + subIdx + 1}\n`
      })
    }
  })

  const tocText: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: tocContent,
    editable: true,
    deletable: false,
  }

  return [tocText]
}

// Enhanced Test Overview with Accreditation Status
// export const getEnhancedTestOverview = (
//   examination: Examination,
//   report: Report,
//   oldData: DataNode[]
// ): DataNode[] => {
//   const accreditationStatus = report.accreditationStatus || AccreditationStatus.ACCREDITED

//   const accreditationNotice = {
//     [AccreditationStatus.ACCREDITED]: "",
//     [AccreditationStatus.NON_ACCREDITED]: "*",
//     [AccreditationStatus.EXTERNAL_ACCREDITED]: "**",
//     [AccreditationStatus.EXTERNAL_NON_ACCREDITED]: "***",
//   }[accreditationStatus]

//   const accreditationText = `This test report may contain test methods which are not part of our accredited test areas. These tests are marked with an asterisk (*). Tests implemented in an accredited external laboratory are marked with two asterisks (**). Tests implemented in a non-accredited external laboratory are marked with three asterisks (***).\n\nText written in *italic type* is external information, not provided by TREO. This can be for example results or additional information provided by the client. The information is not part of results witnessed by TREO, hence TREO is not in charge for the content.\n\nOperating Mode (OM) described a specified setup and state of operation of one or more Equipment Under Test (EUT) and one or more Auxiliary Equipment (AE) as described in section 3 of this document.`

//   const texts: DataNode = {
//     ...emptyDataNode,
//     dataType: NodeDataType.TEXT,
//     id: v4(),
//     text: accreditationText,
//     editable: false,
//     deletable: false,
//   }

//   // Rest of the existing test overview logic...
//   const logisticTable: DataNode = {
//     ...emptyDataNode,
//     dataType: NodeDataType.TABLE,
//     id: v4(),
//     editable: false,
//     deletable: false,
//     tableNumber: createTableNumber(),
//     tableCaption: "Logistics",
//     tableHeader: [
//       { data: "EUT No.", colSpan: 1 },
//       { data: "Date", colSpan: 1 },
//       { data: "Description", colSpan: 1 },
//     ],
//     tableBody: [
//       [
//         { data: "01", colSpan: 1 },
//         { data: report.releaseDate || new Date().toISOString().split("T")[0], colSpan: 1 },
//         { data: `Delivery at ${report.testFacility || "TREO-Neumünster"}`, colSpan: 1 },
//       ],
//       [
//         { data: "01", colSpan: 1 },
//         { data: report.releaseDate || new Date().toISOString().split("T")[0], colSpan: 1 },
//         { data: "Collected by the Client", colSpan: 1 },
//       ],
//     ],
//   }

//   const getTestResults = () => {
//     const datas = examination.tasks
//       .flatMap((task) => task.testDatas)
//       .filter((test) => resultTasks.includes(test.type))
//     const deviceTestData = datas.flatMap((item) => item.deviceTestData)
//     return deviceTestData
//   }

//   const oldTableBody = oldData.length > 0 && isNodeTable(oldData[0]) ? oldData[0].tableBody : []
//   const section = 4 + oldTableBody.length

//   // Determine responsible tester: prefer examination.tester, fallback to report.tester[0]
//   const examTesterName = (() => {
//     const t = (examination as any)?.tester as any
//     if (!t) return null
//     if (t.EmployeeName) return t.EmployeeName as string
//     const first = t.ContactInfo?.FirstName || ""
//     const last = t.ContactInfo?.LastName || ""
//     const full = `${first} ${last}`.trim()
//     return full || null
//   })()

//   const responsiblePerson =
//     examTesterName ||
//     (report.tester?.[0]
//       ? `${report.tester[0]?.firstName || ""} ${report.tester[0]?.lastName || ""}`.trim()
//       : null)

//   const resultBody = [
//     { data: section.toString() + accreditationNotice, colSpan: 1 },
//     { data: `${examination.name}\n\n${examination.norm?.name || ""}`, colSpan: 1 },
//     { data: formatMissingValue(responsiblePerson), colSpan: 1 },
//     { data: new Date().toISOString().split("T")[0], colSpan: 1 },
//     {
//       data: getTestResults()
//         .map((item) => "OM 01\n" + item.device.barcode)
//         .join("\n"),
//       colSpan: 1,
//     },
//     {
//       data: getTestResults()
//         .map((item) => getResultTypeNameForReport(item.result))
//         .join("\n"),
//       colSpan: 1,
//     },
//   ]

//   const resultsTable: DataNode = {
//     ...emptyDataNode,
//     dataType: NodeDataType.TABLE,
//     id: v4(),
//     editable: false,
//     deletable: false,
//     tableCaption: "Results",
//     tableNumber: createTableNumber(),
//     tableHeader: [
//       { data: "Sec.", colSpan: 1 },
//       { data: "Test", colSpan: 1 },
//       { data: "Responsible Person", colSpan: 1 },
//       { data: "Date", colSpan: 1 },
//       { data: "EUT No.", colSpan: 1 },
//       { data: "Result", colSpan: 1 },
//     ],
//     tableBody: [...oldTableBody, [...resultBody]],
//   }

//   return [resultsTable, texts, logisticTable]
// }

// Pass/Fail Criteria Definitions
export const getPassFailCriteriaDefinitions = (): DataNode[] => {
  const criteriaTable: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableNumber: createTableNumber(),
    tableCaption: "Pass/Fail Criteria for Immunity tests",
    tableHeader: [
      { data: "Criteria", colSpan: 1 },
      { data: "Description", colSpan: 1 },
    ],
    tableBody: [
      [
        { data: "A", colSpan: 1 },
        {
          data: "Normal performance within the specification limits specified by the manufacturer, requestor or purchaser",
          colSpan: 1,
        },
      ],
      [
        { data: "B", colSpan: 1 },
        {
          data: "Temporary degradation or loss of function or performance which is self-recoverable after the disturbance ceases",
          colSpan: 1,
        },
      ],
      [
        { data: "C", colSpan: 1 },
        {
          data: "Temporary degradation or loss of function or performance which requires operator intervention or system reset",
          colSpan: 1,
        },
      ],
      [
        { data: "D", colSpan: 1 },
        {
          data: "Degradation or loss of function which is not recoverable due to damage of equipment or software, or loss of data",
          colSpan: 1,
        },
      ],
    ],
  }

  return [criteriaTable]
}

// Operating Mode Descriptions
export const getOperatingModeDescriptions = (examination: Examination): DataNode[] => {
  const operatingModes =
    examination.deviceSetup
      ?.filter((ds) => isDeviceEut(ds.device))
      ?.map((ds, index) => ({
        mode: `OM ${String(index + 1).padStart(2, "0")}`,
        description: ds.device?.name || "Operating mode description",
      })) || []

  if (operatingModes.length === 0) {
    operatingModes.push({
      mode: "OM 01",
      description: "ON, no active Alarm",
    })
  }

  const modeDescriptions = operatingModes
    .map((mode) => `**${mode.mode}:** "${mode.description}"`)
    .join("\n\n")

  const modesText: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `### Operating Mode Descriptions\n\n${modeDescriptions}`,
    editable: true,
    deletable: false,
  }

  return [modesText]
}

// Enhanced Test Location with Facility Information
// export const getEnhancedTestLocation = (examination: Examination, report: Report): DataNode[] => {
//   const datas = examination.tasks
//     .flatMap((task) => task.testDatas)
//     .filter((test) => setUpTasks.includes(test.type))

//   // Check if any ambient conditions passed (true)
//   const ambientConditionsPassed = datas.some((item) => item.ambientConditions === true)

//   const newData: DataNode = {
//     ...emptyDataNode,
//     id: v4(),
//     dataType: NodeDataType.TABLE,
//     editable: false,
//     deletable: false,
//     tableCaption: "Laboratory Ambient Conditions",
//     tableHeader: [
//       { data: "Temperature [°C]", colSpan: 1 },
//       { data: "Ambient Pressure abs.[mbar]", colSpan: 1 },
//       { data: "Relative Humidity [% RH]", colSpan: 1 },
//     ],
//     tableBody: [
//       [
//         { data: "+15 to +35", colSpan: 1 },
//         { data: "860 to 1060", colSpan: 1 },
//         { data: "30 to 75", colSpan: 1 },
//       ],
//     ],
//   }

//   const location: DataNode = {
//     ...emptyDataNode,
//     id: v4(),
//     dataType: NodeDataType.TEXT,
//     editable: false,
//     deletable: false,
//     text: `Test Facility: ${report.testFacility || "TREO – Neumünster"}${ambientConditionsPassed ? "\n\nLaboratory Ambient Conditions:" : ""}`,
//   }

//   // Only show the table if ambient conditions passed
//   return datas.length > 0 ? (ambientConditionsPassed ? [location, newData] : [location]) : []
// }

// Export all the existing functions from buildReport.ts
export * from "./buildReport"
